// File: src/components/PriceRangeSlider.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import "../styles/PriceRangeSlider.css";

const ABSOLUTE_MIN = 50000;
const ABSOLUTE_MAX = 7000000;

const fmt = (price) => {
  if (price >= 10000000) return `₹${(price / 10000000).toFixed(1)} Cr`;
  if (price >= 100000)   return `₹${(price / 100000).toFixed(1)}L`;
  if (price >= 1000)     return `₹${(price / 1000).toFixed(0)}k`;
  return `₹${price}`;
};

const pct = (v) =>
  Math.round(((v - ABSOLUTE_MIN) / (ABSOLUTE_MAX - ABSOLUTE_MIN)) * 100);

const PriceRangeSlider = ({ minPrice, maxPrice, onPriceChange }) => {
  const [minVal, setMinVal] = useState(minPrice ?? ABSOLUTE_MIN);
  const [maxVal, setMaxVal] = useState(maxPrice ?? ABSOLUTE_MAX);
  const timerRef = useRef(null);

  // Sync when parent resets
  useEffect(() => {
    setMinVal(minPrice ?? ABSOLUTE_MIN);
    setMaxVal(maxPrice ?? ABSOLUTE_MAX);
  }, [minPrice, maxPrice]);

  // Debounced callback so we don't fire on every pixel move
  const debouncedChange = useCallback((min, max) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onPriceChange(min, max), 400);
  }, [onPriceChange]);

  const handleMin = (e) => {
    const v = Math.min(Number(e.target.value), maxVal - 50000);
    setMinVal(v);
    debouncedChange(v, maxVal);
  };

  const handleMax = (e) => {
    const v = Math.max(Number(e.target.value), minVal + 50000);
    setMaxVal(v);
    debouncedChange(minVal, v);
  };

  const minPct = pct(minVal);
  const maxPct = pct(maxVal);

  return (
    <div className="prs-container">
      {/* Values row */}
      <div className="prs-values">
        <div className="prs-val-box">
          <span className="prs-val-label">Min</span>
          <span className="prs-val-amount">{fmt(minVal)}</span>
        </div>
        <div className="prs-separator">—</div>
        <div className="prs-val-box prs-val-right">
          <span className="prs-val-label">Max</span>
          <span className="prs-val-amount">{fmt(maxVal)}</span>
        </div>
      </div>

      {/* Slider track */}
      <div className="prs-track-wrap">
        <div className="prs-track-bg" />
        <div
          className="prs-track-fill"
          style={{ left: `${minPct}%`, width: `${maxPct - minPct}%` }}
        />
        <input
          type="range"
          className="prs-thumb"
          min={ABSOLUTE_MIN} max={ABSOLUTE_MAX} step={10000}
          value={minVal}
          onChange={handleMin}
          style={{ zIndex: minVal > ABSOLUTE_MAX - 100000 ? 5 : 3 }}
        />
        <input
          type="range"
          className="prs-thumb"
          min={ABSOLUTE_MIN} max={ABSOLUTE_MAX} step={10000}
          value={maxVal}
          onChange={handleMax}
          style={{ zIndex: 4 }}
        />
      </div>

      {/* Range labels */}
      <div className="prs-range-labels">
        <span>{fmt(ABSOLUTE_MIN)}</span>
        <span>{fmt(ABSOLUTE_MAX)}</span>
      </div>
    </div>
  );
};

export default PriceRangeSlider;